import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { currentBranding } from '../../../config/branding';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { resetAllSlices } from '../../../redux/slice/ReduxResetSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneNumber from '../../../shared-components/custom-phone-number-input/CustomPhoneNumberInput';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import { setCookie, getCookie } from '../../../shared-functions/Cookies';
import {
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import {
  emailValidation,
  // passwordValidation,
  phoneNumberValidation,
  textAndNumberValidation,
} from '../../../shared-functions/Validation';
import auth from '../../../utils/api/auth';
import BannerWithSlider from '../../register/components/BannerWithSlider';

/**
 * Login Component
 */
function LoginComponent() {
  const { t } = useTranslation('login');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParameters] = useSearchParams();
  const accountCustomId = getCookie('accountCustomId');

  const [credentials, setCredentials] = useState<{
    emailOrPhoneNumber: string;
    password: string;
    accountCustomId: string;
    type: string | number;
    countryCode: string;
  }>({
    emailOrPhoneNumber: '',
    password: '',
    accountCustomId: accountCustomId || '',
    type: 'email',
    countryCode: currentBranding === 'energic' ? '966' : '1',
  });

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  /**
   * Get input change value and store in credentials state
   * @param name
   * @param changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    if (name === 'type') {
      setCredentials({
        ...credentials,
        [name]: changeValue,
        emailOrPhoneNumber: '',
      });
    } else {
      setCredentials({
        ...credentials,
        [name]: changeValue,
      });
    }
  };

  /**
   * Function to handle the login success
   */
  const loginSuccessHandler = (response: any) => {
    // Store the verification status in local storage
    setLocalStorageItem('accountVerificationStatus', response?.accountVerificationStatus);

    // Store access token inside cookies
    setCookie('ev-access-token', response?.access?.token, null, response?.access?.expires);

    // Store refresh token inside cookies
    setCookie('ev-refresh-token', response?.refresh?.token, null, response?.refresh?.expires);

    const userVerification = {
      redirectToEmailVerification: response?.redirectToEmailVerification,
      redirectToPhoneVerification: response?.redirectToPhoneVerification,
      isInitialPasswordReset: response?.isInitialPasswordReset,
    };

    // Set verification in local storage to use in email verification page
    setLocalStorageItem('userVerification', userVerification);

    setFormStatus({
      showFormErrors: false,
      buttonLoader: false,
    });

    // Navigate to next page based on email and phone number verification
    if (response?.redirectToEmailVerification) {
      navigate('../login/email-verification');
    } else if (response?.redirectToPhoneVerification) {
      navigate('../login/phone-number-verification');
    } else if (!response?.isInitialPasswordReset) {
      navigate('../login/reset-initial-password');
    } else if (
      !response?.redirectToEmailVerification &&
      !response?.redirectToPhoneVerification &&
      response?.isInitialPasswordReset
    ) {
      removeLocalStorageItem('userVerification'); // If the user is verified remove the verification data in local storage
      if (response?.accountVerificationStatus === 'approved') {
        navigate('../ev-admin/dashboard');
      } else {
        navigate('../ev-admin/profile');
      }
    } else {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 401,
            message: 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * Function to submit entered cre
   */
  const handleSubmit = () => {
    setFormStatus({
      showFormErrors: true,
      buttonLoader: false,
    });
    if (
      credentials.type !== '' &&
      credentials.countryCode !== '' &&
      textAndNumberValidation(20, 20, credentials.accountCustomId) &&
      (phoneNumberValidation(credentials.emailOrPhoneNumber) ||
        emailValidation(credentials.emailOrPhoneNumber))
      // passwordValidation(credentials.password)
    ) {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: true,
      });
      const request = {
        countryCode: credentials.countryCode,
        accountCustomId: credentials.accountCustomId,
        emailOrPhoneNumber: credentials.emailOrPhoneNumber,
        password: credentials.password,
      };
      auth
        .login(request)
        .then((response) => {
          setCookie('accountCustomId', credentials.accountCustomId, null, null);
          loginSuccessHandler(response?.data);
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });

          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: 200, // Code is not required, if we pass code as 404 means then page will redirect to login (Just as refresh)
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    }
  };

  useEffect(() => {
    // Access specific query parameters
    const accessToken = searchParameters.get('accessToken');
    const refreshToken = searchParameters.get('refreshToken');
    const accountVerificationStatus = searchParameters.get('accountVerificationStatus');
    const accessTokenExpires = searchParameters.get('accessTokenExpires');
    const refreshTokenExpires = searchParameters.get('refreshTokenExpires');
    const redirectToEmailVerification = searchParameters.get('redirectToEmailVerification');
    const redirectToPhoneVerification = searchParameters.get('redirectToPhoneVerification');
    const isInitialPasswordReset = searchParameters.get('isInitialPasswordReset');

    if (
      accessToken &&
      refreshToken &&
      accountVerificationStatus &&
      accessTokenExpires &&
      refreshTokenExpires
    ) {
      loginSuccessHandler({
        accountVerificationStatus,
        access: {
          token: accessToken,
          expires: accessTokenExpires,
        },
        refresh: {
          token: refreshToken,
          expires: refreshTokenExpires,
        },
        redirectToEmailVerification,
        redirectToPhoneVerification,
        isInitialPasswordReset,
      });
    }
  }, [location.search, searchParameters]);

  useEffect(() => {
    dispatch(resetAllSlices());
    const accessToken = getCookie('ev-access-token');
    if (accessToken) {
      navigate('../ev-admin/dashboard');
    }
  }, []);

  return (
    <div className="login-container">
      <div className="login-banner-container">
        <BannerWithSlider />
      </div>
      <div className="login-form-container">
        <div className="login-form">
          <div className="login-form-header">
            <h1>{t('signIn')}</h1>
          </div>
          <CustomInput
            inputLabel="Account Id"
            type="text"
            placeholder="Enter the account Id"
            name="accountCustomId"
            value={credentials.accountCustomId}
            onChangeValue={handleCredentialChange}
            error={
              !textAndNumberValidation(20, 20, credentials.accountCustomId) &&
              formStatus.showFormErrors
            }
            errorMessage="Invalid Account ID"
          />
          <RadioButton
            name="type"
            value={credentials.type}
            list={[
              {
                label: 'Email',
                value: 'email',
              },
              {
                label: 'Phone Number',
                value: 'phone',
              },
            ]}
            onChangeValue={handleCredentialChange}
          />
          {credentials.type === 'email' ? (
            <CustomInput
              inputLabel="Email"
              type="text"
              placeholder="Enter the email"
              name="emailOrPhoneNumber"
              value={credentials.emailOrPhoneNumber}
              onChangeValue={handleCredentialChange}
              error={!emailValidation(credentials.emailOrPhoneNumber) && formStatus.showFormErrors}
              errorMessage="Invalid email id"
            />
          ) : (
            <CustomPhoneNumber
              inputLabel="Phone Number"
              placeholder="Enter the Phone number"
              name="emailOrPhoneNumber"
              value={credentials.emailOrPhoneNumber}
              onChangeValue={handleCredentialChange}
              error={
                !phoneNumberValidation(credentials.emailOrPhoneNumber) && formStatus.showFormErrors
              }
              errorMessage="Invalid phone number"
              countryCode={credentials.countryCode}
            />
          )}
          <div className="password-container">
            <CustomInput
              inputLabel="Password"
              type="password"
              placeholder="Enter password"
              name="password"
              value={credentials.password}
              onChangeValue={handleCredentialChange}
              // error={!passwordValidation(credentials.password) && formStatus.showFormErrors}
              // errorMessage="Password must be at least 12 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character."
            />
            <div className="forgot-password">
              <span role="presentation" onClick={() => navigate('../login/forgot-password')}>
                Forgot Password?
              </span>
            </div>
          </div>
          <MyButton
            label="Submit"
            buttonType="submit"
            onClickFunc={handleSubmit}
            loading={formStatus.buttonLoader}
            disableButton={formStatus.buttonLoader}
          />
          <div className="register-account">
            <span>Don&apos;t have an account?</span>
            <span role="presentation" onClick={() => navigate('../register')}>
              Register
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginComponent;
